
import {defineComponent, PropType} from "vue";
import {CodeDescriptionRestDto} from "@/api/pharma-cms-content-block/models";
import {ColorValueRestDtoModel} from "@/models/api/ColorValueRestDtoModel";
import {ColorRestDtoModel} from "@/models/api/ColorRestDtoModel";
import {ColorGradientRestDtoModel} from "@/models/api/ColorGradientRestDtoModel";
import ColorPicker from "@/components/UI/ColorPicker.vue";
import InputSelect from "@/components/UI/InputSelect.vue";
import {ComponentMixin} from "@/components/base/ComponentMixin";
import {EnumRestService} from "@/services/rest/enum/EnumRestService";
import {EnumClassNameCmsEnum} from "@/models/enum/EnumClassNameCmsEnum";
import {DpModuleEnum} from "@/api/enums/dp-module-enum";
import {ColorModeEnum} from "@/models/enum/ColorModeEnum";
import GenericComponentWrapper from "@/components/base/GenericComponentWrapper.vue";
import {TextColorValueRestDtoModel} from "@/models/api/TextColorValueRestDtoModel";
import {ColorValueRestDto, TextColorValueRestDto} from "@/api/pharma-cms-content-page/models";
import {TextColorGradientRestDtoModel} from "@/models/api/TextColorGradientRestDtoModel";
import ColorSolidEditor from "@/components/layouts/content-pages/wysiwyg/misc/ColorSolidEditor.vue";

export default defineComponent({
  name: "AdvancedColorEditor",
  mixins: [ComponentMixin],
  components: {ColorSolidEditor, GenericComponentWrapper, ColorPicker, InputSelect},
  emits: ['update:modelValue'],
  props: {
    modelValue: {
      type: Object as PropType<ColorValueRestDto | TextColorValueRestDto>,
      required: true,
    },
    label: {
      type: String,
      required: false,
      default: '',
    },
    name: {
      type: String,
      required: true,
    },
    isBackground: {
      type: Boolean,
      required: false,
      default: false
    },
    errors: {
      type: Object,
      required: true,
    },
  },
  mounted() {
    this.initialize();
  },
  data() {
    return {
      localDto: (this.isBackground
        ? ColorValueRestDtoModel.createWithDefaults()
        : TextColorValueRestDtoModel.createWithDefaults()) as ColorValueRestDto | TextColorValueRestDto,
      enumTextColorCode: [] as CodeDescriptionRestDto[],
      enumBackgroundColorCode: [] as CodeDescriptionRestDto[],
      enumGradientDirectionCode: [] as CodeDescriptionRestDto[],
    };
  },
  watch: {
    modelValue: {
      immediate: true,
      handler(newValue: ColorValueRestDto | TextColorValueRestDto) {
        if (this.isBackground) {
          this.localDto = ColorValueRestDtoModel.cloneFrom(newValue);
        } else {
          this.localDto = TextColorValueRestDtoModel.cloneFrom(newValue);
        }
      },
    },
  },
  computed: {
    ColorModeEnum() {
      return ColorModeEnum;
    },
    colorMode(): ColorModeEnum {
      if (this.localDto.rgba) {
        return ColorModeEnum.RGBA;
      }
      if (this.localDto.gradient) {
        return ColorModeEnum.GRADIENT;
      }
      return ColorModeEnum.TOKEN;
    },
  },
  methods: {
    async doInitialize(): Promise<void> {
      [
        this.enumTextColorCode,
        this.enumBackgroundColorCode,
        this.enumGradientDirectionCode,
      ] = await Promise.all([
        EnumRestService.getInstance().getEnumValuesByClassName(EnumClassNameCmsEnum.TEXT_COLOR, DpModuleEnum.CMS),
        EnumRestService.getInstance().getEnumValuesByClassName(EnumClassNameCmsEnum.BACKGROUND_COLOR, DpModuleEnum.CMS),
        EnumRestService.getInstance().getEnumValuesByClassName(EnumClassNameCmsEnum.GRADIENT_DIRECTION, DpModuleEnum.CMS),
      ]);
    },
    setColorMode(mode: ColorModeEnum): void {
      if (mode === ColorModeEnum.TOKEN) {
        this.localDto.rgba = null;
        this.localDto.gradient = null;
      } else if (mode === ColorModeEnum.RGBA) {
        this.clearColorCode();
        this.localDto.gradient = null;
        if (!this.localDto.rgba) {
          this.localDto.rgba = ColorRestDtoModel.createWithDefaults();
        }
      } else {
        this.clearColorCode();
        this.localDto.rgba = null;
        if (!(this.localDto as ColorValueRestDto | TextColorValueRestDto).gradient) {
          if (this.isBackground) {
            (this.localDto as ColorValueRestDto).gradient = ColorGradientRestDtoModel.createWithDefaults();
          } else {
            (this.localDto as TextColorValueRestDto).gradient = TextColorGradientRestDtoModel.createWithDefaults();
          }
        }
      }
      this.emitModelChanged();
    },
    clearColorCode(): void {
      if (this.isBackground) {
        (this.localDto as ColorValueRestDto).color_code = null;
      } else {
        (this.localDto as TextColorValueRestDto).text_color_code = null;
      }
    },
    emitModelChanged(): void {
      this.$emit('update:modelValue', this.localDto);
    }
  },
});
